/**
 * The landing page — what someone sees at thegoodstock.co.uk before signing in.
 *
 * Direction D, "stone and green", with the green-sweep background, approved by
 * Emmanuel on 10 October 2026 (docs/DECISIONS.md). It replaced a sign-in form
 * beside a dark panel with three ticked bullet points, which said nothing a
 * visitor could see or check.
 *
 * ## It shows the product instead of describing it
 *
 * The cards in the hero and the "inside the app" section are drawn from the
 * same example clinic as docs/test-data/sample-products.csv, and each is
 * labelled as an example. They are illustrations of real screens, with numbers
 * that add up — not screenshots, and not claims about anyone's results.
 *
 * ## What is deliberately not here
 *
 * No customer counts, no testimonials, no "trusted by". There are none yet, and
 * an invented number is the one thing on a landing page that can only hurt.
 * Nothing unshipped either: no barcode scanning, no WhatsApp alerts.
 *
 * ## Log in and Sign up are one click away, not on this page
 *
 * The form moved to its own screen (AuthScreen). Every route into the product
 * is unchanged; this page only decides what a visitor reads first.
 */
import { PRICING, SUPPORT_EMAIL, TRIAL_DAYS, formatGBP } from '@inventory-tracker/core';
import { c, display, mono, sans } from '../theme.js';
import { GreenSweep, Wordmark } from './AuthLayout.js';
import { HeroCard } from './ExampleCard.js';
import { PricingSection } from './PricingSection.js';

interface Props {
  onLogin: () => void;
  onSignup: () => void;
}

const button = (kind: 'green' | 'white' | 'ghost'): React.CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: 48,
  padding: '0 22px',
  borderRadius: 980,
  border: kind === 'ghost' ? '1px solid rgba(237,235,228,0.4)' : 'none',
  background: kind === 'green' ? c.green : kind === 'white' ? c.white : 'transparent',
  color: kind === 'green' ? c.white : kind === 'white' ? c.ink : c.onDark,
  fontFamily: sans,
  fontSize: 16,
  fontWeight: 500,
  cursor: 'pointer',
  textDecoration: 'none',
  whiteSpace: 'nowrap',
});

const grey = '#85887E';

export function LandingPage({ onLogin, onSignup }: Props) {
  const fromPrice = formatGBP(PRICING.starter.monthly);

  function toPricing() {
    document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
  }

  return (
    <div style={{ background: c.frame, padding: 12, fontFamily: sans, color: c.ink }}>
      <style>{`
        .gs-hero-h1 { font-size: clamp(44px, 7.4vw, 104px); }
        .gs-hero-foot { display: flex; align-items: flex-end; justify-content: space-between; gap: 40px; }
        .gs-cards { display: flex; gap: 8px; }
        .gs-card { width: 262px; }
        .gs-three { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 8px; }
        .gs-why-h2 { font-size: clamp(30px, 4.2vw, 56px); }
        /* Below this width the hero is taller than the curve can follow, so the
           curve is dropped and the foot of the hero carries its own green. Left
           as it was, the pale "why" line sat on pale stone and could not be read. */
        @media (max-width: 1180px) {
          .gs-sweep { display: none; }
          .gs-hero-foot {
            flex-direction: column; align-items: stretch;
            margin: 0 -18px -18px; padding: 32px 18px 18px;
            background: radial-gradient(90% 60% at 60% 0%, #3F6B52 0%, #24402F 40%, #16241C 100%);
          }
          .gs-cards { flex-wrap: wrap; }
          .gs-card { flex: 1 1 240px; width: auto; }
        }
        @media (max-width: 480px) {
          .gs-nav-btn { padding: 0 12px !important; }
        }
        @media (max-width: 760px) {
          .gs-three { grid-template-columns: 1fr; }
          .gs-why-line { color: #F1EFE8 !important; }
        }
      `}</style>

      {/* ------------------------------------------------------------ hero */}
      <header
        style={{
          position: 'relative',
          minHeight: 'calc(100vh - 24px)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          padding: '14px 18px 18px',
          background: `radial-gradient(56% 48% at 50% 30%, #F1EFE8 0%, rgba(241,239,232,0) 72%), ${c.authBg}`,
        }}
      >
        <div className="gs-sweep">
          <GreenSweep />
        </div>

        <nav
          aria-label="Account"
          style={{
            position: 'relative',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <Wordmark />
          <div style={{ display: 'flex', gap: 6 }}>
            <button
              type="button"
              className="gs-nav-btn"
              onClick={onLogin}
              style={{ ...button('green'), height: 44, padding: '0 18px', fontSize: 15 }}
            >
              Log in
            </button>
            <button
              type="button"
              className="gs-nav-btn"
              onClick={onSignup}
              style={{ ...button('white'), height: 44, padding: '0 18px', fontSize: 15 }}
            >
              Sign up
            </button>
          </div>
        </nav>

        <div
          style={{
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            padding: '56px 0 48px',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 14.5,
              color: '#3A423C',
            }}
          >
            <span
              aria-hidden="true"
              style={{
                width: 15,
                height: 15,
                borderRadius: 980,
                background: c.terracotta,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <svg
                width="10"
                height="10"
                viewBox="0 0 12 12"
                fill="none"
                stroke="#FFFFFF"
                strokeWidth="2"
              >
                <path d="M2.5 6.2l2.4 2.4 4.6-5" />
              </svg>
            </span>
            <span>Stock control, built for clinics</span>
          </div>

          <h1
            className="gs-hero-h1"
            style={{ ...display, margin: '16px 0 0', letterSpacing: '-0.054em', lineHeight: 1.02 }}
          >
            Know what expires
            <br />
            <span style={{ color: grey }}>before</span> it costs you
          </h1>

          <p
            style={{
              margin: '20px 0 0',
              fontSize: 17,
              lineHeight: 1.45,
              color: '#5F6259',
              maxWidth: 330,
            }}
          >
            Every batch, use-by date and reorder for your clinic, in one place.
          </p>

          <div
            style={{
              marginTop: 26,
              display: 'flex',
              gap: 6,
              flexWrap: 'wrap',
              justifyContent: 'center',
            }}
          >
            <button type="button" onClick={onSignup} style={button('green')}>
              Start free trial
            </button>
            <button type="button" onClick={toPricing} style={button('white')}>
              See pricing
            </button>
          </div>
        </div>

        <div className="gs-hero-foot" style={{ position: 'relative', marginTop: 'auto' }}>
          <div style={{ maxWidth: 360, padding: '0 0 4px 8px' }}>
            <div style={{ fontSize: 13, fontWeight: 500, color: '#B9C4BB', marginBottom: 10 }}>
              Why
            </div>
            <p
              className="gs-why-line"
              style={{
                margin: 0,
                fontSize: 21,
                fontWeight: 300,
                lineHeight: 1.22,
                letterSpacing: '-0.02em',
                color: '#F1EFE8',
              }}
            >
              The Good Stock logs every batch as it arrives,{' '}
              <span style={{ color: '#B4BEB5' }}>
                uses the oldest first and tells you what to reorder before you run out.
              </span>
            </p>
          </div>

          <div className="gs-cards" aria-label="Example figures from a sample clinic">
            <HeroCard
              title="Going out of date"
              sub="in the next 12 weeks"
              figure="£ 705.50"
              tag={{ text: '1 expired', tone: 'danger' }}
              tagNote="across 4 batches"
              rows={[
                ['Lidocaine 5%', 'expired', true],
                ['Azzalure 125u', '18 days'],
                ['Heliocare 360', '41 days'],
              ]}
            />
            <HeroCard
              title="Stock on hand"
              sub="40 units, 8 products"
              figure="£ 2,035.90"
              tag={{ text: '3 types', tone: 'green' }}
              tagNote="by value"
              rows={[
                ['Treatment products', '£1,444.00'],
                ['Retail', '£522.00'],
                ['Consumables', '£69.90'],
              ]}
            />
            <HeroCard
              title="To reorder"
              sub="at or below minimum"
              figure="2"
              figureUnit="products"
              tag={{ text: '1 out of stock', tone: 'danger' }}
              rows={[
                ['Sculptra', 'none left', true],
                ['Azzalure 125u', '2 of 3'],
                ['Example clinic', ''],
              ]}
            />
          </div>
        </div>
      </header>

      {/* ------------------------------------------------------------- why */}
      <section
        style={{ background: '#141A16', color: c.onDark, padding: 'clamp(64px, 10vw, 128px) 24px' }}
      >
        <div style={{ maxWidth: 900, margin: '0 auto', textAlign: 'center' }}>
          <Tag onDark>why the good stock</Tag>
          <h2
            className="gs-why-h2"
            style={{
              ...display,
              margin: '22px 0 0',
              lineHeight: 1.1,
              letterSpacing: '-0.04em',
              color: '#DAD8D0',
            }}
          >
            A box of toxin is money with a use-by date.{' '}
            <span style={{ color: '#7E877F' }}>
              A spreadsheet will not tell you which one to open first, or that one went out of date
              last week.
            </span>
          </h2>
        </div>

        <div
          className="gs-three"
          style={{ maxWidth: 980, margin: 'clamp(48px, 7vw, 88px) auto 0', gap: 0 }}
        >
          {[
            [
              'Oldest first',
              'Log a use and the batch closest to its date is the one that goes down.',
            ],
            [
              'Every movement kept',
              'Who took what, when and why. Mistakes are reversed, never erased.',
            ],
            [
              'No patient data',
              'It counts stock and treatments. It never holds anything about a patient.',
            ],
          ].map(([title, body], i) => (
            <div
              key={title}
              style={{
                padding: '8px 28px',
                borderLeft: i === 0 ? 'none' : '1px solid #2A332C',
                textAlign: 'center',
              }}
            >
              <div
                style={{
                  ...display,
                  fontSize: 30,
                  fontWeight: 300,
                  letterSpacing: '-0.03em',
                  color: '#DAD8D0',
                }}
              >
                {title}
              </div>
              <p style={{ margin: '10px 0 0', fontSize: 15, lineHeight: 1.5, color: '#A9B5AC' }}>
                {body}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* ---------------------------------------------------- inside the app */}
      <section style={{ background: c.appBg, padding: 'clamp(56px, 8vw, 104px) 24px' }}>
        <div style={{ maxWidth: 1080, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 'clamp(32px, 5vw, 56px)' }}>
            <Tag>inside the app</Tag>
            <h2 style={{ ...display, fontSize: 'clamp(34px, 5vw, 64px)', margin: '20px 0 0' }}>
              Every batch, <span style={{ color: grey }}>one shelf</span>
            </h2>
            <p
              style={{
                margin: '14px auto 0',
                fontSize: 17,
                color: c.muted,
                maxWidth: 460,
                lineHeight: 1.5,
              }}
            >
              What you would see on a Saturday morning at an example clinic.
            </p>
          </div>

          <div className="gs-three">
            <Feature
              title="Use these first"
              body="Batches in the order they go out of date, so nothing sits at the back of the fridge."
            >
              <MiniRow name="Lidocaine 5%" lot="LD-2609" tag="Expired" tone="danger" />
              <MiniRow name="Azzalure 125u" lot="AZ-2610" tag="18 days" tone="amber" />
              <MiniRow name="Heliocare 360" lot="HC-2611" tag="41 days" tone="amber" />
              <MiniRow name="Juvederm Voluma" lot="JV-2703" tag="In date" tone="ok" last />
            </Feature>
            <Feature
              title="Reorder before you run out"
              body="Set a minimum for each product. You are told when stock reaches it, in the app and by email."
            >
              <MiniRow name="Sculptra" lot="minimum 1" tag="None left" tone="danger" />
              <MiniRow name="Azzalure 125u" lot="minimum 3" tag="2 left" tone="amber" />
              <MiniRow name="Profhilo 2ml" lot="minimum 2" tag="4 left" tone="ok" last />
            </Feature>
            <Feature
              title="A count that explains itself"
              body="Count the shelf, give a reason for any difference, and the record shows who changed what."
            >
              <MiniRow
                name="Nitrile gloves"
                lot="system 12, counted 11"
                tag="Breakage"
                tone="amber"
              />
              <MiniRow name="C E Ferulic 30ml" lot="system 5, counted 5" tag="Matches" tone="ok" />
              <MiniRow name="Profhilo 2ml" lot="system 4, counted 4" tag="Matches" tone="ok" last />
            </Feature>
          </div>
        </div>
      </section>

      {/* ----------------------------------------------------------- pricing */}
      <PricingSection />

      {/* -------------------------------------------------------- closing CTA */}
      <section
        style={{
          background: '#141A16',
          color: c.onDark,
          padding: 'clamp(64px, 9vw, 120px) 24px 0',
          overflow: 'hidden',
        }}
      >
        <div style={{ textAlign: 'center' }}>
          <h2
            style={{
              ...display,
              fontSize: 'clamp(32px, 4.6vw, 60px)',
              margin: 0,
              color: '#DAD8D0',
            }}
          >
            Your next stocktake, <span style={{ color: '#7E877F' }}>already half done.</span>
          </h2>
          <p style={{ margin: '16px 0 0', fontSize: 16, color: '#A9B5AC' }}>
            Free for {TRIAL_DAYS} days. No card. Then from {fromPrice} a month.
          </p>
          <div
            style={{
              marginTop: 26,
              display: 'flex',
              gap: 6,
              justifyContent: 'center',
              flexWrap: 'wrap',
            }}
          >
            <button
              type="button"
              onClick={onSignup}
              style={{ ...button('white'), background: '#DAD8D0' }}
            >
              Start free trial
            </button>
            <button type="button" onClick={onLogin} style={button('ghost')}>
              Log in
            </button>
          </div>
        </div>

        <footer
          style={{
            maxWidth: 1080,
            margin: 'clamp(56px, 8vw, 96px) auto 0',
            borderTop: '1px solid #2A332C',
            padding: '28px 0 0',
            display: 'flex',
            justifyContent: 'space-between',
            gap: 24,
            flexWrap: 'wrap',
            fontSize: 14.5,
            color: '#A9B5AC',
          }}
        >
          <Wordmark onDark />
          <div>Your data is held in the UK.</div>
          <a href={`mailto:${SUPPORT_EMAIL}`} style={{ color: '#A9B5AC' }}>
            {SUPPORT_EMAIL}
          </a>
          <div style={{ display: 'flex', gap: 18 }}>
            <a href="/terms" style={{ color: '#A9B5AC' }}>
              Terms
            </a>
            <a href="/privacy" style={{ color: '#A9B5AC' }}>
              Privacy
            </a>
          </div>
          <div>© {new Date().getFullYear()} The Good Stock</div>
        </footer>

        <div
          aria-hidden="true"
          style={{
            ...display,
            fontSize: 'clamp(90px, 21vw, 320px)',
            fontWeight: 500,
            letterSpacing: '-0.06em',
            lineHeight: 0.8,
            whiteSpace: 'nowrap',
            color: '#1B231E',
            margin: '40px 0 -0.16em -0.02em',
            userSelect: 'none',
          }}
        >
          the good stock
        </div>
      </section>
    </div>
  );
}

function Tag({ children, onDark = false }: { children: string; onDark?: boolean }) {
  return (
    <span
      style={{
        display: 'inline-block',
        fontFamily: mono,
        fontSize: 11,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        padding: '5px 9px',
        borderRadius: 980,
        background: onDark ? '#DAD8D0' : c.ink,
        color: onDark ? c.ink : '#EDEBE4',
      }}
    >
      {children}
    </span>
  );
}

function Feature({
  title,
  body,
  children,
}: {
  title: string;
  body: string;
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        background: c.card,
        borderRadius: 18,
        padding: 8,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{ background: c.greenDeep, borderRadius: 12, padding: '18px 16px', minHeight: 196 }}
      >
        <div style={{ background: c.card, borderRadius: 12, padding: '4px 12px' }}>{children}</div>
      </div>
      <div style={{ padding: '18px 12px 14px', textAlign: 'center' }}>
        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 500, letterSpacing: '-0.02em' }}>
          {title}
        </h3>
        <p style={{ margin: '8px 0 0', fontSize: 14.5, lineHeight: 1.5, color: c.muted }}>{body}</p>
      </div>
    </div>
  );
}

function MiniRow({
  name,
  lot,
  tag,
  tone,
  last = false,
}: {
  name: string;
  lot: string;
  tag: string;
  tone: 'danger' | 'amber' | 'ok';
  last?: boolean;
}) {
  const tones = {
    danger: { background: c.danger, color: c.white },
    amber: { background: c.amberBg, color: c.amberText },
    ok: { background: c.okBg, color: c.okText },
  } as const;
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 10,
        padding: '9px 0',
        borderBottom: last ? 'none' : `1px solid ${c.border}`,
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 500 }}>{name}</div>
        <div style={{ fontFamily: mono, fontSize: 11, color: c.muted2 }}>{lot}</div>
      </div>
      <span
        style={{
          ...tones[tone],
          fontSize: 12,
          padding: '3px 8px',
          borderRadius: 980,
          whiteSpace: 'nowrap',
        }}
      >
        {tag}
      </span>
    </div>
  );
}
